import { css, html, LitElement, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { scripter } from '../modules/scripter';

export class DebugInfo extends LitElement {

    static styles = css`
        :host {
            display: block;
            margin: 4em auto;
            box-sizing: border-box;
            width: 90%;
            max-width: 1600px;
        }

        header {
            margin-bottom: 3em;
        }

        header span:first-child {
            display: inline-block;
            text-align: right;
            width: 8rem;
            border-right: 1px black solid;
            margin-right: 1rem;
            padding-right: 1rem;
            color: #666666;
            box-sizing: border-box;
        }

        header h2,
        header h3 {
            color: var(--krypton-font-color-header);
        }
    `;

    render() {
        return html`
            <header>
                <h4>${this.action}</h4>
                <h2><span>Module</span>${this.module}</h2>
                <h3><span>Page</span>${this.page}</h3>
                <h3><span>Access</span>${this.access}</h3>
            </header>

            <button @click="${this.openSimplePage}">Open module simple subpage</button>
            <button @click="${this.openFormPage}">Open module form page</button>
            <button @click="${this.openSliderPage}">Open module slider page</button>

            <slot name="namespaces"></slot>
            <slot name="scripts"></slot>
            <slot></slot>
        `;
    }

    get access() {
        let p = this.parentElement as any;
        while (p && !p.access) p = p?.parentElement;
        return p?.access;
    }

    get action() {
        let p = this.parentElement as any;
        while (p && !p.action) p = p?.parentElement;
        return p?.action;
    }

    get module() {
        let p = this.parentElement as any;
        while (p && !p.module) p = p?.parentElement;
        return p?.module;
    }

    get page() {
        let p = this.parentElement as any;
        while (p && !p.page) p = p?.parentElement;
        return p?.page;
    }

    connectedCallback() {
        this.createScript();
        super.connectedCallback();
    }

    firstUpdated(changedProperties: PropertyValues) {
        const mNamepsace = new NamespaceInfo(this.module);
        const pNamepsace = new NamespaceInfo(this.module, this.page);
        this.appendChild(pNamepsace);
        this.appendChild(mNamepsace);
        super.firstUpdated(changedProperties);
    }

    createScript(): HTMLScriptElement | void {

        const script = document.createElement("script");
        script.setAttribute("slot", "scripts");
        script.setAttribute("module", this.module);
        script.setAttribute("page", this.page);
        script.textContent = '\
        console.log("namespace: ' + this.module + '.' + this.page + '");\
        _.moduleLevelFunction = function () { alert("module level function: ' + this.module + '") };\
        __.pageLevelFunction = function() { alert("page level function: ' + this.page + '") };';

        const scripterResult = scripter(script);

        if (!scripterResult)
            return;

        this.appendChild(scripterResult);
        return
    }

    async openSimplePage() {
        //@ts-ignore
        let page = await kryptonApp().fetchPage("/WebDispatch/page-simple.html");
        //@ts-ignore
        return await kryptonApp().showPage(page, true);
    }

    async openFormPage() {
        //@ts-ignore
        let page = await kryptonApp().fetchPage("/WebDispatch/page-form.html");
        //@ts-ignore
        return await kryptonApp().showPage(page, true);
    }

    async openSliderPage() {
         //@ts-ignore
         let page = await kryptonApp().fetchPage("/WebDispatch/page-slider.html");
         //@ts-ignore
         return await kryptonApp().showPage(page, true);
    }


}

declare global {
    interface HTMLElementTagNameMap {
        'debug-info': DebugInfo;
    }
}

export class NamespaceInfo extends LitElement {

    @property({ type: String }) accessor namespaceId;
    @property({ type: Object }) accessor namespace;

    static styles = css`
        h1 {
            font-size: 1em;
            padding: 0;
            margin: 1em 0;
        }

        .code {
            border: 1px #666 dashed;
            padding: 1.5em 1em;
            background-color: #f3f3f3;
            font-family: consolas;
            box-sizing: border-box;
        }
    `;

    constructor(module: string, page?: string) {
        super();
        this.setAttribute("slot", "namespaces");
        this.namespace = window["krypton"]["module"][module];
        this.namespaceId = "kryptonApp().module." + module;
        if (page) {
            this.namespace = this.namespace["page"][page];
            this.namespaceId = this.namespaceId + ".page." + page;
        }

    }

    render() {
        return html`
        <article>
            <h1>${this.namespaceId}</h1>
            <div class="code"></div>
        </article>`;
    }

    firstUpdated(changedProperties: PropertyValues) {
        this.namespaceToHTML(this.namespace);
        super.firstUpdated(changedProperties);
    }

    namespaceToHTML(namespaceObj: Object) {
        for (let [key, value] of Object.entries(namespaceObj)) {
            switch (key) {
                case "page":
                    continue;
                default:
                    let namespaceFunction = new NamespaceFunction(this.namespace, key, value.toString());
                    this.shadowRoot?.querySelector(".code")?.appendChild(namespaceFunction);
            }
        }
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'namespace-info': NamespaceInfo;
    }
}

export class NamespaceFunction extends LitElement {

    @property({ type: String }) accessor name;
    @property({ type: String }) accessor body;
    @property({ type: Object }) accessor namespace;

    constructor(namespace: Object, name: string, body: string) {
        super();
        this.namespace = namespace;
        this.name = name;
        this.body = body;
    }

    static styles = css`
        .name {
            color: #184b6b;
            cursor: pointer;
        }

        .name::after {
            content: ': ';
        }

        .name:hover {
            font-weight: bold;
            color: #34c0eb;
        }
        `;

    render() {
        return html`
            <span class="name" @click="${this.call}">${this.name}</span>
            <span class="body">${this.body}</span>
        `;
    }

    call() {
        this.namespace[this.name]();
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'namespace-function': NamespaceFunction;
    }
}